import { z } from "zod";
import { AccountId, ApprovalId, SubWalletId, WalletId } from "./ids.ts";
import { MoneyAmount } from "./money.ts";
import { TransactionMetadata } from "./transaction.ts";

/**
 * Lifecycle of a master-approval request (#29). A spend above a sub-wallet's
 * `approvalThreshold` (or any agent spend, per policy) parks here as `pending`
 * until the master responds. `expired` is a terminal timeout state (set by a
 * sweep when `expiresAt` passes); it never silently becomes `approved`.
 */
export const ApprovalStatus = z.enum(["pending", "approved", "denied", "expired"]);
export type ApprovalStatus = z.output<typeof ApprovalStatus>;

/**
 * What the approval authorizes. `transfer` carries the full P2P intent
 * (recipient + idempotency key) so approving it commits the cross-account
 * posting; `spend` is the advisory agent/authorize decision with no destination.
 */
export const ApprovalKind = z.enum(["spend", "transfer"]);
export type ApprovalKind = z.output<typeof ApprovalKind>;

/**
 * A master-approval request: "the agent/human on sub-wallet X wants to spend
 * `amount`; does the master allow it?". The master gets a mobile push (#9/#29
 * transport, deferred) and approves/denies, which resolves this record.
 *
 * Modeled as a discriminated union on `kind` so the intent invariant is type-
 * AND runtime-enforced: a `transfer` MUST carry `recipientWalletId` +
 * `idempotencyKey` (approving commits that exact send); a `spend` (advisory
 * agent authorize) MUST have both `null`. Invalid combinations can't be
 * constructed or parsed.
 */
const ApprovalBase = z.object({
  id: ApprovalId,
  accountId: AccountId,
  /** Master wallet whose owner approves — the push target. */
  walletId: WalletId,
  /** Sub-wallet requesting the spend. */
  subWalletId: SubWalletId,
  amount: MoneyAmount,
  status: ApprovalStatus,
  /** Optional context shown to the approver (e.g. "agent: groceries"). */
  reason: z.string().max(280).nullable(),
  requestedAt: z.coerce.date(),
  /** Set when the master approves/denies (or a sweep expires it); null while pending. */
  resolvedAt: z.coerce.date().nullable(),
  /** After this instant a pending request is no longer actionable. */
  expiresAt: z.coerce.date(),
});

/** Advisory agent/authorize approval — no destination to commit, and (like the
 * other transfer-intent fields) no metadata. */
export const SpendApproval = ApprovalBase.extend({
  kind: z.literal("spend"),
  recipientWalletId: z.null(),
  idempotencyKey: z.null(),
  metadata: z.null(),
});

/** Parked P2P transfer — approving posts it to `recipientWalletId`. Carries the
 * same optional `metadata` an immediately-posted transfer would (#191), so
 * approving replays the identical send; `null` when the transfer had none. */
export const TransferApproval = ApprovalBase.extend({
  kind: z.literal("transfer"),
  recipientWalletId: WalletId,
  idempotencyKey: z.string(),
  metadata: TransactionMetadata.nullable(),
});

export const Approval = z.discriminatedUnion("kind", [SpendApproval, TransferApproval]);
export type Approval = z.output<typeof Approval>;
